import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import CartItem from '../../components/customer/CartItem';
import {
  ShoppingCart,
  ArrowRight,
  ArrowLeft,
  Trash2,
  Shield,
  Truck,
  ChevronRight
} from 'lucide-react';
import CartSummary from '../../components/customer/CartSummary';
import { formatPrice } from '../../utils/utilityFunctions';

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem, clearCart, validateCart, isLoading, refreshStock } = useCart();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    // Clear errors on mount
    setValidationErrors([]);

    const validateCartOnLoad = async () => {
      if (cart.items.length === 0) return;
      setIsValidating(true);
      try {
        // Force fresh stock fetch
        await refreshStock();
        const { valid, errors } = await validateCart();
        setValidationErrors(valid ? [] : errors);
      } catch (error) {
        console.error('Failed to validate cart:', error);
      } finally {
        setIsValidating(false);
      }
    };

    validateCartOnLoad();
  }, []);

  const handleProceedToCheckout = async () => {
    if (cart.items.length === 0) return;
    console.log('Proceeding to checkout with cart:', cart);
    setIsValidating(true);
    try {
      const { valid, errors } = await validateCart();
      if (!valid) {
        setValidationErrors(errors);
        return;
      }
      setValidationErrors([]);
      navigate('/checkout');
    } catch (error) {
      console.error('Checkout validation failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleUpdateQuantity = async (itemId: string, quantity: number) => {
    try {
      await updateQuantity(itemId, quantity);
      // const { valid, errors } = await validateCart();
      // setValidationErrors(valid ? [] : errors);
    } catch (error: any) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId);
    setValidationErrors(prev => prev.filter(error => !error.includes(itemId)));
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-100 mb-6">
              <ShoppingCart className="h-10 w-10 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Your cart is empty</h1>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Browse our tech collection and add some awesome products to get started!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all hover:shadow-lg"
              >
                <ArrowRight className="mr-2 h-5 w-5" />
                Start Shopping
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Mobile breadcrumb */}
        <div className="lg:hidden mb-6">
          <nav className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-gray-900 font-medium">Cart</span>
          </nav>
        </div>

        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Main content */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
              {/* Header */}
              <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h1 className="text-xl font-bold text-gray-900">
                    Your Cart
                  </h1>
                  <div className="text-sm text-gray-500">
                    Subtotal: <span className="font-semibold text-gray-900">{formatPrice(cart.subtotal)}</span>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="px-4 sm:px-6 py-4 bg-red-50 border-b border-red-100">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                      <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 text-sm">!</span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Cart issues</h3>
                      <ul className="mt-1 text-sm text-red-700 space-y-1">
                        {validationErrors.slice(0, 2).map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                        {validationErrors.length > 2 && (
                          <li className="text-red-600">
                            +{validationErrors.length - 2} more issues
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Cart Items - Compact on mobile */}
              <div className="divide-y divide-gray-200">
                {cart.items.map((item) => (
                  <div key={item.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex gap-4">
                      {/* Product image - smaller on mobile */}
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-lg overflow-hidden">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.product_title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ShoppingCart className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Product details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col h-full">
                          {/* Title and price row */}
                          <div className="flex justify-between items-start mb-2">
                            <Link
                              to={`/product/${item.product_id}`}
                              className="text-sm sm:text-base font-medium text-gray-900 hover:text-blue-600 line-clamp-2 flex-1 mr-2"
                            >
                              {item.product_title}
                            </Link>
                            <div className="text-right">
                              <div className="text-sm sm:text-base font-bold text-gray-900">
                                {formatPrice(item.price * item.quantity)}
                              </div>
                              {item.quantity > 1 && (
                                <div className="text-xs text-gray-500">
                                  {formatPrice(item.price)} each
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Product Description - New section */}
                          {item.product_description && (
                            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-2">
                              {item.product_description}
                            </p>
                          )}

                          {/* Variant and SKU */}
                          <div className="mb-3">
                            {item.variant_name && item.variant_name !== 'Default' && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded mr-2">
                                {item.variant_name}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              SKU: {item.sku}
                            </span>
                          </div>

                          {/* Quantity controls and actions */}
                          <div className="flex items-center justify-between mt-auto">
                            {/* Quantity selector - compact */}
                            <div className="flex items-center border border-gray-300 rounded-lg">
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1 || isLoading}
                                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                              >
                                −
                              </button>
                              <span className="w-10 text-center text-sm font-medium">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                disabled={isLoading || item.quantity >= item.available_quantity}
                                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100"
                              >
                                +
                              </button>
                            </div>

                            {/* Remove button */}
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              disabled={isLoading}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              aria-label="Remove item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Continue shopping button - mobile optimized */}
            <div className="lg:hidden mb-6">
              <Link
                to="/products"
                className="w-full inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Continue Shopping
              </Link>
            </div>

            {/* Features - mobile grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <Truck className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Free Delivery</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Orders over $100 in Port of Spain
                      </p>
                    </div>
                  </div>
                </div> */}
              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-4 border border-green-100">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <Shield className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Cash or Card on Delivery</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Pay when you receive your order
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary - Sticky on mobile */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-6">
              {/* Mobile summary - always visible at bottom or separate section */}
              <div className="lg:hidden bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatPrice(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery</span>
                    <span className="font-medium">
                      {cart.subtotal > 100 ? 'FREE' : '$25.00'}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between text-base font-bold">
                      <span>Total</span>
                      <span>{formatPrice((cart.subtotal + (cart.subtotal > 100 ? 0 : 25)))}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleProceedToCheckout}
                  disabled={isValidating || cart.items.length === 0}
                  className="w-full mt-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
                >
                  {isValidating ? 'Checking...' : 'Proceed to Checkout'}
                </button>
              </div>

              {/* Desktop summary */}
              <div className="hidden lg:block">
                <CartSummary
                  showCheckoutButton={true}
                  validationErrors={validationErrors}
                  isProcessing={isValidating}
                />
              </div>

              {/* Clear cart button - desktop only */}
              <div className="hidden lg:flex mt-4">
                <button
                  onClick={clearCart}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Clear Entire Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;